import throttle from "lodash/throttle";
import Script from "next/script";
import { createRef, Component } from "react";
import type { VideoJsPlayer } from "video.js";
import videojs from "video.js";
import "videojs-contrib-ads";
import "videojs-ima";

import type { UserSong } from "db/schemas/userSong";
import EventManager from "util/EventManager";
import DEBUG from "util/debugSettings";
import rootLogger from "util/logger";
import { playerLogger } from "util/playerLogger";

import CircuitBreaker from "./CircuitBreaker";
import type { Context } from "./Context";
import MusicPlayerContext from "./Context";
import MediaSessionManager from "./MediaSessionManager";
import PlayedSong from "./PlayedSong";
import {
  PlaybackPositionContext,
  PlayerStateContext,
  PlayerActionsContext,
} from "./SplitContexts";
import type {
  PlayerStateContextType,
  PlayerActionsContextType,
} from "./SplitContexts";
import type { MusicPlayerProps } from "./types";
import { NextSongReason } from "./types";

// Keep the original logger for compatibility with existing methods
const logger = rootLogger.getLogger("MusicPlayer");

interface State {
  duration: number;
  muted: boolean;
  position: number;
  stateContextValue: PlayerStateContextType;
  actionsContextValue: PlayerActionsContextType;
}

export default class MusicPlayer extends Component<MusicPlayerProps, State> {
  unlockedAudio = false;
  currentSong?: UserSong;
  eventManager = new EventManager();
  isPlaying = false;
  mediaSessionManager: MediaSessionManager;
  playbackStats?: PlayedSong;
  isFirstPlay = true;
  suppressFirstPlayWarning = true;

  player?: VideoJsPlayer;
  circuitBreaker = createRef<CircuitBreaker>();

  private throttledSeek = throttle((position: number) => {
    if (this.player) {
      this.player.currentTime(position);
    }
  }, 200);

  // Create stable references for context values
  private createStateContextValue(): PlayerStateContextType {
    const { currentSong, playerState, playlist, shuffleMode, volume } =
      this.props;
    const { muted } = this.state || { muted: false };

    return {
      currentSong,
      playerState,
      playlist,
      shuffleMode,
      muted,
      volume,
    };
  }

  private createActionsContextValue(): PlayerActionsContextType {
    const {
      currentSongUpdated,
      nextSong,
      shuffleModeToggled,
      songAddedToLibrary,
      songDownloaded,
      songDisliked,
      songLiked,
      songPlayed,
      songShared,
      volumeUpdated,
    } = this.props;

    return {
      currentSongUpdated,
      muteToggled: this.muteToggled,
      playToggled: this.playToggled,
      shuffleModeToggled,
      songAddedToLibrary,
      songDownloaded,
      songDisliked,
      songLiked,
      songPlayed,
      songShared,
      songSkipped: () => nextSong(NextSongReason.Skip),
      volumeUpdated,
    };
  }

  private progressUpdated = (position: number, duration: number) => {
    // Only update position and duration, nothing else
    this.setState({ position, duration });
  };

  private muteToggled = () => {
    const nextMuted = !this.state.muted;
    if (nextMuted) {
      this.setVolume(0);
    } else {
      this.setVolume(this.props.volume);
    }

    // Update muted state and the state context value
    this.setState((prevState) => ({
      muted: nextMuted,
      stateContextValue: {
        ...prevState.stateContextValue,
        muted: nextMuted,
      },
    }));
  };

  private step = () => {
    if (this.player && this.isPlaying) {
      const duration = this.player.duration();
      const position = this.player.currentTime();

      // only update the position when it changes
      if (position != this.state.position) {
        if (DEBUG.POSITION_UPDATE_TIMING) {
          // With timing measurements enabled
          const startTotal = performance.now();

          const startProgress = performance.now();
          this.progressUpdated(position, duration);
          const progressTime = performance.now() - startProgress;

          const startStats = performance.now();
          this.playbackStats?.updatePosition(position, duration);
          const statsTime = performance.now() - startStats;

          const startMedia = performance.now();
          this.mediaSessionManager.updatePosition(position, duration);
          const mediaTime = performance.now() - startMedia;

          const totalTime = performance.now() - startTotal;
          console.log(`Position update timing (ms):
            - progressUpdated: ${progressTime.toFixed(2)}
            - playbackStats: ${statsTime.toFixed(2)}
            - mediaSessionManager: ${mediaTime.toFixed(2)}
            - Total: ${totalTime.toFixed(2)}`);
        } else {
          // Normal execution without timing
          this.progressUpdated(position, duration);
          this.playbackStats?.updatePosition(position, duration);
          this.mediaSessionManager.updatePosition(position, duration);
        }
      }

      // IMPORTANT: Always schedule the next step, even if position hasn't changed.
      // This is crucial for Safari compatibility. Safari may not report position changes
      // for the first ~10 seconds after playback starts, especially on initial app load.
      // By continuing to poll regardless of position changes, we ensure that:
      // 1. Safari eventually starts reporting correct positions after its internal
      //    media element becomes fully activated
      // 2. The track slider and time display continue to update properly
      // 3. Play statistics are recorded correctly
      //
      // If we only scheduled the next step when position changed (the original behavior),
      // Safari would often stop updating position entirely for the first song after launch.

      // In addition, we poll at a high frequency (100ms) to ensure smooth waveform drawing across all browsers.

      // In Safari, lower polling frequencies can cause visual artifacts in the waveform display,
      // where the position indicator flickers or shows inconsistent coloring at the edge.
      // This is a temporary solution - the root cause of Safari's waveform drawing issues
      // has not yet been determined.

      setTimeout(this.step, 100);
    }
  };

  private updateMediaSessionMetadata(song: UserSong | undefined) {
    if (!song?.title || !song?.artist) {
      playerLogger.critical.debug(
        "Cannot update MediaSession metadata - missing song data",
        { song }
      );
      return;
    }

    try {
      this.mediaSessionManager.updateMetadata(song);
    } catch (error) {
      playerLogger.critical.debug("Failed to update MediaSession metadata", {
        error,
        song,
      });
    }
  }

  private playInternal = async () => {
    if (this.player != null) {
      if (this.isPlaying) {
        if (!this.suppressFirstPlayWarning) {
          logger.warn("player.play() being called when already playing");
        } else {
          this.suppressFirstPlayWarning = false;
        }
        // If already playing, we can return early to avoid duplicate play calls
        return;
      }

      try {
        const songInfo = this.currentSong
          ? `"${this.currentSong.title}" by ${this.currentSong.artist}`
          : "unknown song";
        playerLogger.critical.debug(
          `Calling player.play() from playInternal - Song: ${songInfo}`
        );
        await this.player.play();
      } catch (error) {
        // Check if this is an AbortError from a play request interrupted by a new load request
        if (
          error instanceof Error &&
          error.name === "AbortError" &&
          error.message.includes(
            "The play() request was interrupted by a new load request"
          )
        ) {
          // Silently ignore this specific error as it's expected when rapidly changing songs
          playerLogger.critical.debug(
            "Ignoring expected AbortError from interrupted play request"
          );
        } else if (
          error instanceof Error &&
          error.name === "AbortError" &&
          (error.message.includes(
            "The play() request was interrupted by a call to pause()"
          ) ||
            error.message.includes("The operation was aborted"))
        ) {
          // Silently ignore these common abort scenarios unless debug is enabled
          if (DEBUG.PLAYER_IMPLEMENTATION) {
            playerLogger.critical.debug(
              "playInternal error when calling play =",
              error
            );
          }
        } else {
          // Log any other errors as they might be important
          playerLogger.critical.debug(
            "playInternal error when calling play =",
            error
          );
        }
        // Don't re-throw the error since we've handled it
      }
    }
  };

  private pauseInternal = async () => {
    if (this.player != null) {
      if (this.player.paused()) {
        logger.warn("player.pause() being called when already paused");
      }
      try {
        const songInfo = this.currentSong
          ? `"${this.currentSong.title}" by ${this.currentSong.artist}`
          : "unknown song";
        playerLogger.critical.debug(
          `Calling player.pause() from pauseInternal - Song: ${songInfo}`
        );
        await this.player.pause();
      } catch (error) {
        // While pause() doesn't typically throw AbortError, we'll handle errors consistently
        playerLogger.critical.debug(
          "pauseInternal error when calling pause =",
          error
        );
      }
    }
  };

  private changeSong = async (song: UserSong) => {
    const { authToken, currentSongUpdated, volume } = this.props;

    if (this.isPlaying) {
      this.pauseInternal();
    }

    this.isPlaying = false;
    if (!this.player) {
      logger.warn("No player instance available");
      return;
    }

    // MUST update first else we may end up in a componentDidUpdate loop.
    this.currentSong = song;
    this.playbackStats = new PlayedSong(song.id);
    currentSongUpdated(song);

    this.player.src({
      src: `/api/playSong?id=${song.id}&token=${authToken}`,
      type: "audio/mpeg",
    });

    this.player.volume(volume);

    await this.player.load();
    playerLogger.state.debug(
      `player.load(), song = ${song.title} by ${song.artist}`
    );

    await this.playInternal().catch((error) => {
      // Check if this is an AbortError from a play request interrupted by a new load request
      if (
        error instanceof Error &&
        error.name === "AbortError" &&
        error.message.includes(
          "The play() request was interrupted by a new load request"
        )
      ) {
        // Silently ignore this specific error as it's expected when rapidly changing songs
        playerLogger.critical.debug(
          "Ignoring expected AbortError from interrupted play request"
        );
      } else if (
        error instanceof Error &&
        error.name === "AbortError" &&
        (error.message.includes(
          "The play() request was interrupted by a call to pause()"
        ) ||
          error.message.includes("The operation was aborted"))
      ) {
        // Silently ignore common abort errors unless debug is enabled
        if (DEBUG.PLAYER_IMPLEMENTATION) {
          playerLogger.critical.debug("changeSong play error =", error);
        }
      } else {
        // Log any other errors as they might be important
        playerLogger.critical.debug("changeSong play error =", error);
      }
    });

    // fixes bug #227, first play gets stuck
    if (this.isFirstPlay) {
      if (DEBUG.FIRST_PLAY_MESSAGES) {
        playerLogger.critical.debug("firstPlay is called twice");
      }
      this.playInternal()?.catch((error) => {
        // Check if this is an AbortError from a play request interrupted by a new load request
        if (
          error instanceof Error &&
          error.name === "AbortError" &&
          error.message.includes(
            "The play() request was interrupted by a new load request"
          )
        ) {
          // Silently ignore this specific error as it's expected when rapidly changing songs
          playerLogger.critical.debug(
            "Ignoring expected AbortError from interrupted play request in firstPlay"
          );
        } else {
          // Log any other errors as they might be important
          playerLogger.critical.debug(
            "firstPlay changeSong play error =",
            error
          );
        }
      });
      this.isFirstPlay = false;
    }
  };

  play = (index = 0) => {
    const { songPlayed, playlist } = this.props;

    this.playbackStats?.songFinished(songPlayed);

    const song = playlist[index];
    if (song) {
      this.changeSong(song);
    } else {
      logger.error(`No song at index ${index}`);
    }
  };

  // playSong = (id: string) => {
  //   const { playlist } = this.props;
  //   const index = playlist.findIndex((o) => o.id === id);
  //   if (index >= 0) {
  //     this.play(index);
  //   } else {
  //     logger.error(`No song with id ${id}`);
  //   }
  // };

  songSeeked = (position: number) => {
    if (this.player) {
      const currentPosition = this.player.currentTime();
      if (this.isPlaying) {
        this.playbackStats?.updateSkippedRanges({
          from: currentPosition,
          to: position,
        });
      }
      this.throttledSeek(position);
    }
  };

  seekBackward = (offset: number) => {
    if (this.player) {
      const currentPosition = this.player.currentTime();
      const position = Math.max(currentPosition - offset, 0);
      this.songSeeked(position);
    }
  };

  seekForward = (offset: number) => {
    if (this.player) {
      const currentPosition = this.player.currentTime();
      const duration = this.player.duration();
      const position = Math.min(currentPosition + offset, duration);
      this.songSeeked(position);
    }
  };

  playToggled = () => {
    if (this.props.playerState === "playing") {
      this.pauseInternal();
      this.isPlaying = false;
    } else {
      this.playInternal().catch((error) => {
        // Error handling is already done in playInternal
        // We just need this catch to prevent unhandled promise rejection
        // The error is already logged appropriately in playInternal
      });
    }
    this.props.playToggled();
  };

  setVolume = (volume: number) => {
    if (this.player) {
      this.player.volume(volume);
    }
  };

  getPosition = () => this.state.position;

  getDuration = () => this.state.duration;

  // The mediaSessionManager is an optional parameter to facilitate testing
  constructor(props: MusicPlayerProps, context?: Context) {
    super(props, context);

    // Initialize state with stable context values
    this.state = {
      duration: 0,
      muted: false,
      position: 0,
      stateContextValue: this.createStateContextValue(),
      actionsContextValue: this.createActionsContextValue(),
    };

    this.mediaSessionManager = new MediaSessionManager({
      onNext: props.nextSong.bind(null, NextSongReason.Skip),
      onPlay: this.playToggled,
      onSeekBackward: this.seekBackward,
      onSeekForward: this.seekForward,
      songSeeked: this.songSeeked,
    });
  }

  async componentDidMount() {
    console.log("MusicPlayer: componentDidMount called");
    const cb = Math.round(Math.random() * 1_000_000_000_000);
    this.player = videojs("player", { controls: false });

    this.player.on("ended", () => {
      playerLogger.state.debug(`ended ${this.currentSong?.title}`);
      if (this.props.playerState !== "paused") {
        // only call nextSong when we are not paused
        playerLogger.critical.debug(
          `Song finished playing: "${this.currentSong?.title}". Auto-skipping to next track...`
        );
        this.props.nextSong(NextSongReason.Ended);
      }
    });

    this.player.on("error", (error) => {
      this.isPlaying = false; // Reset playing state on error
      playerLogger.critical.debug(
        `Player error for song "${this.currentSong?.title}":`,
        error
      );
      if (this.circuitBreaker.current?.failure(this.player?.error() || null)) {
        this.playToggled();
        this.props.stopPlayback();
      } else {
        this.props.nextSong(NextSongReason.Error);
      }
    });

    this.player.on("playing", () => {
      if (this.currentSong === undefined) {
        playerLogger.state.debug(`started playing ${this.player?.src()}`);
      } else {
        playerLogger.state.debug(
          `started playing ${this.currentSong?.title} by ${this.currentSong?.artist}`
        );
        this.updateMediaSessionMetadata(this.currentSong);
      }
      this.isPlaying = true;
      setTimeout(this.step, 100);
    });

    this.player.on("seeked", () => {
      playerLogger.impl.debug(
        `seeked ${this.currentSong?.title} to ${this.player?.currentTime()}`
      );
      if (this.player && !this.isPlaying) {
        const duration = this.player.duration();
        const position = this.player.currentTime();

        console.log("seeked", this.player?.currentTime());
        if (position != this.state.position) {
          this.throttledSeek(this.player?.currentTime() || 0);
          this.progressUpdated(position, duration);
          this.mediaSessionManager.updatePosition(position, duration);
        }
      }
    });

    // NOTE: We don't need explicit timeupdate handler as the step() function
    // already polls for position changes regularly. The timeupdate event in Safari
    // can be unreliable during the initial activation period after page load.
  }

  componentDidUpdate(
    prevProps: Readonly<MusicPlayerProps>,
    prevState: Readonly<State>
  ) {
    //console.log("MusicPlayer: componentDidUpdate called");
    // Check for changes that would require updating context values
    const shouldUpdateStateContext =
      prevProps.currentSong !== this.props.currentSong ||
      prevProps.playerState !== this.props.playerState ||
      prevProps.playlist !== this.props.playlist ||
      prevProps.shuffleMode !== this.props.shuffleMode ||
      prevProps.volume !== this.props.volume;

    const shouldUpdateActionsContext =
      prevProps.currentSongUpdated !== this.props.currentSongUpdated ||
      prevProps.nextSong !== this.props.nextSong ||
      prevProps.shuffleModeToggled !== this.props.shuffleModeToggled ||
      prevProps.songAddedToLibrary !== this.props.songAddedToLibrary ||
      prevProps.songDownloaded !== this.props.songDownloaded ||
      prevProps.songDisliked !== this.props.songDisliked ||
      prevProps.songLiked !== this.props.songLiked ||
      prevProps.songPlayed !== this.props.songPlayed ||
      prevProps.songShared !== this.props.songShared ||
      prevProps.volumeUpdated !== this.props.volumeUpdated;

    // Only update the context values when relevant props change
    if (shouldUpdateStateContext && !shouldUpdateActionsContext) {
      this.setState({
        stateContextValue: this.createStateContextValue(),
      });
    } else if (!shouldUpdateStateContext && shouldUpdateActionsContext) {
      this.setState({
        actionsContextValue: this.createActionsContextValue(),
      });
    } else if (shouldUpdateStateContext && shouldUpdateActionsContext) {
      this.setState({
        stateContextValue: this.createStateContextValue(),
        actionsContextValue: this.createActionsContextValue(),
      });
    }

    // Handle existing behavior for player state changes
    // Check if the playlist has changed
    const playlistChanged = prevProps.playlist !== this.props.playlist;
    const currentSongChanged =
      this.currentSong?.id !== this.props.currentSong?.id;

    if (playlistChanged || currentSongChanged) {
      // Only log when meaningful changes occur
      if (playlistChanged) {
        playerLogger.state.debug(
          "Playlist changed from",
          prevProps.playlist.length,
          "to",
          this.props.playlist.length,
          "songs"
        );
      }

      if (currentSongChanged) {
        playerLogger.critical.debug(
          "Current song changed from",
          this.currentSong?.title,
          "to",
          this.props.currentSong?.title
        );

        // Only trigger play() if the current song has changed
        if (this.props.currentSong) {
          const index = this.props.playlist.findIndex(
            (o) => o.id === this.props.currentSong?.id
          );

          playerLogger.impl.debug("componentDidUpdate: index =", index);
          if (index >= 0) {
            this.play(index);
          }
        }
      }
    }

    if (
      prevProps.playerState &&
      prevProps.playerState !== this.props.playerState
    ) {
      if (!this.player) {
        logger.warn("No player instance available");
        return;
      }

      // Handle play/pause
      if (this.props.playerState === "paused") {
        if (this.player && this.isPlaying) {
          playerLogger.state.debug("Player paused");
          this.pauseInternal()?.catch((error) => {
            // While pause() doesn't typically throw AbortError, we'll handle errors consistently
            playerLogger.critical.debug(
              "componentDidUpdate error when calling pause =",
              error
            );
          });
        }
      } else {
        if (this.player && !this.isPlaying) {
          playerLogger.critical.debug(
            "componentDidUpdate: calling playInternal. this.props.playerState",
            this.props.playerState,
            "this.isPlaying",
            this.isPlaying
          );

          this.playInternal().catch((error) => {
            // Check if this is an AbortError from a play request interrupted by a new load request
            if (
              error instanceof Error &&
              error.name === "AbortError" &&
              error.message.includes(
                "The play() request was interrupted by a new load request"
              )
            ) {
              // Silently ignore this specific error as it's expected when rapidly changing songs
              playerLogger.critical.debug(
                "Ignoring expected AbortError from interrupted play request"
              );
            } else if (
              error instanceof Error &&
              error.name === "AbortError" &&
              (error.message.includes(
                "The play() request was interrupted by a call to pause()"
              ) ||
                error.message.includes("The operation was aborted"))
            ) {
              // Silently ignore common abort errors unless debug is enabled
              if (DEBUG.PLAYER_IMPLEMENTATION) {
                playerLogger.critical.debug(
                  "componentDidUpdate error when calling play",
                  error
                );
              }
            } else {
              // Log any other errors as they might be important
              playerLogger.critical.debug(
                "componentDidUpdate error when calling play",
                error
              );
            }
          });
        }
      }
      this.mediaSessionManager.updatePlayerState(this.props.playerState);
    }

    if (prevProps.volume !== this.props.volume) {
      this.setVolume(this.props.volume);
    }
  }

  componentWillUnmount() {
    this.eventManager.removeAll();
    this.mediaSessionManager.destroy();
    if (!this.player?.isDisposed()) {
      this.player?.dispose();
    }
  }

  render() {
    const { progressUpdated, songSeeked } = this;
    const { children } = this.props;
    const { duration, position, stateContextValue, actionsContextValue } =
      this.state;

    // Position context is recreated on each render since it's expected to change frequently
    const positionContextValue = {
      position,
      duration,
      progressUpdated,
      songSeeked,
    };

    // Legacy context value for backward compatibility
    const legacyContextValue: Context = {
      ...stateContextValue,
      ...actionsContextValue,
      position,
      duration,
      progressUpdated,
      songSeeked,
    };

    return (
      <MusicPlayerContext.Provider value={legacyContextValue}>
        <PlayerStateContext.Provider value={stateContextValue}>
          <PlayerActionsContext.Provider value={actionsContextValue}>
            <PlaybackPositionContext.Provider value={positionContextValue}>
              <video
                id="player"
                className="video-js"
                preload="auto"
                width="0"
                height="0"
                style={{ display: "none" }}
              />
              {children}
              <CircuitBreaker ref={this.circuitBreaker} />
            </PlaybackPositionContext.Provider>
          </PlayerActionsContext.Provider>
        </PlayerStateContext.Provider>
      </MusicPlayerContext.Provider>
    );
  }
}
